import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

import AIContextTooltip from "@/components/AIContextTooltip";
import { GeminiBottomSheet } from "@/components/GeminiBottomSheet";
import { useVoiceCommands } from "@/hooks/useVoiceCommands";
import { Mic } from "lucide-react";

const HIDDEN_PAGES = ["/auth", "/apple-store", "/tecly-ai"];

const TeclyAIFloatingButton = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [geminiQuery, setGeminiQuery] = useState("");
  const [geminiOpen, setGeminiOpen] = useState(false);
  const [isContextHidden, setIsContextHidden] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isListening, transcript, startListening, stopListening, clearVoiceState, processCommand } = useVoiceCommands();
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLongPress = useRef(false);
  const shouldSuppressClick = useRef(false);
  const hasProcessedRef = useRef(false);

  // Auth subscription
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setIsAuthenticated(!!session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => setIsAuthenticated(!!session));
    return () => subscription.unsubscribe();
  }, []);

  // Full cleanup function
  const clearAIState = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setGeminiOpen(false);
    setGeminiQuery("");
    isLongPress.current = false;
    hasProcessedRef.current = false;
    clearVoiceState();
  }, [clearVoiceState]);

  // Clean up on route change
  useEffect(() => {
    clearAIState();
  }, [location.pathname, clearAIState]);

  // Hide button contextually
  useEffect(() => {
    const updateHiddenState = () => {
      const articleDetailOpen = Boolean(document.querySelector('[data-tecly-article-detail="true"]'));
      const privateChatOpen = Boolean(document.querySelector('[data-tecly-private-chat="true"]'));
      setIsContextHidden(articleDetailOpen || privateChatOpen);
    };
    updateHiddenState();
    const observer = new MutationObserver(updateHiddenState);
    observer.observe(document.body, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ["data-tecly-article-detail", "data-tecly-private-chat"],
    });
    return () => observer.disconnect();
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (longPressTimer.current) clearTimeout(longPressTimer.current); };
  }, []);

  // Process voice result ONCE when listening stops
  useEffect(() => {
    // Only act when listening just stopped and we haven't processed yet
    if (isListening) {
      hasProcessedRef.current = false;
      return;
    }

    // Not listening — check if we need to process
    if (hasProcessedRef.current || !isLongPress.current) return;
    hasProcessedRef.current = true;

    const spokenText = transcript.trim();

    if (!spokenText) {
      clearAIState();
      return;
    }

    const result = processCommand(spokenText);

    if (result.type === "navigate" && result.path) {
      // Navigation command: toast, cleanup, navigate
      toast.success(result.message || "Comando eseguito", { duration: 1000 });
      // Reset everything before navigating to avoid loops
      setGeminiOpen(false);
      setGeminiQuery("");
      isLongPress.current = false;
      hasProcessedRef.current = true;
      clearVoiceState();
      navigate(result.path);
      return;
    }

    // Open question: set query to trigger AI streaming in bottom sheet
    setGeminiQuery(spokenText);
    // Sheet is already open from handlePointerDown
  }, [isListening, transcript, clearAIState, navigate, processCommand, clearVoiceState]);

  const handlePointerDown = useCallback(() => {
    if (location.pathname === "/tecly-ai") return;

    shouldSuppressClick.current = false;
    isLongPress.current = false;
    hasProcessedRef.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      shouldSuppressClick.current = true;
      setGeminiQuery("");
      setGeminiOpen(true);
      startListening();
    }, 420);
  }, [location.pathname, startListening]);

  const handlePointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (isLongPress.current) {
      stopListening();
    }
  }, [stopListening]);

  const handlePointerLeave = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (isLongPress.current) {
      stopListening();
    }
  }, [stopListening]);

  const handleClick = useCallback(() => {
    if (shouldSuppressClick.current) {
      shouldSuppressClick.current = false;
      return;
    }
    clearAIState();
    navigate("/tecly-ai");
  }, [clearAIState, navigate]);

  const handleCloseSheet = useCallback(() => {
    clearAIState();
  }, [clearAIState]);

  // Visibility checks
  const isArticlePage = location.pathname.startsWith("/news/");
  const isMessagingChat = location.pathname.startsWith("/messaggi/");
  if (!isAuthenticated || HIDDEN_PAGES.includes(location.pathname) || isArticlePage || isMessagingChat || isContextHidden) return null;

  return (
    <>
      <AIContextTooltip visible={!geminiOpen} />

      <GeminiBottomSheet
        open={geminiOpen}
        onClose={handleCloseSheet}
        query={geminiQuery}
        transcript={transcript}
        isListening={isListening}
      />

      {(!geminiOpen || isListening) && (
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.92 }}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          onPointerCancel={handlePointerLeave}
          onClick={handleClick}
          data-tecly-ai-btn
          className={`fixed bottom-24 right-4 z-50 w-11 h-11 rounded-full shadow-lg flex items-center justify-center border ${
            isListening
              ? "bg-destructive border-destructive/50 shadow-destructive/40"
              : "bg-gradient-to-br from-primary to-primary/80 border-primary/20 shadow-primary/25 dark:border-primary/40 dark:shadow-primary/40 dark:ring-1 dark:ring-primary/20"
          }`}
          style={{ WebkitTapHighlightColor: "transparent", touchAction: "none" }}
        >
          <div className="relative">
            {isListening ? (
              <Mic className="w-5 h-5 text-white" />
            ) : (
              <>
                <span className="font-black text-base text-primary-foreground select-none" style={{ letterSpacing: "-0.03em" }}>
                  T
                </span>
                <div className="absolute -top-1 -right-1.5 w-2 h-2">
                  <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                    <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" fill="currentColor" className="text-primary-foreground" />
                  </svg>
                </div>
              </>
            )}
          </div>
          {!isListening && (
            <motion.div
              className="absolute inset-0 rounded-full border border-primary/30 dark:border-primary/50"
              animate={{ scale: [1, 1.25, 1], opacity: [0.4, 0, 0.4] }}
              transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            />
          )}
        </motion.button>
      )}
    </>
  );
};

export default TeclyAIFloatingButton;
